import { useCallback, useMemo } from "react";

import {
  UseBaseQueryOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError } from "axios";

import { caseTemplatesApi } from "src/api/endpoints";
import { useCurrentWorkspace } from "src/api/queries";
import {
  CaseBlockConfigOutput,
  JsonApiResourceObjectCaseTemplateAttributesBaseModel as CaseTemplate,
  CaseTemplateJsonSchemaResponse,
  CaseTemplateMetadataUpdate,
  CaseTemplateResponse,
  CaseTemplatesResponse,
  JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel as CaseTemplateVersion,
  CaseTemplateVersionAttributes,
  CaseTemplateVersionPublishState,
  Data,
  JsonApiResourceObjectCaseTemplateAttributesBaseModel,
  JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel,
} from "src/clients/case-management";
import { DashboardPageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

// Constants
const STALE_TIME = 1000 * 60 * 5; // 5 minutes

// Types
export type TemplateWithVersion = {
  templateId: string;
  versionId: string;
  templateName: string;
  versionName: string;
  icon: string | null | undefined;
  template: JsonApiResourceObjectCaseTemplateAttributesBaseModel;
  version: JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel;
};

// Query key factory for cache management
export const caseTemplatesKeys = {
  all: (workspaceId: string) => ["caseTemplates", workspaceId] as const,
  list: (workspaceId: string) =>
    [...caseTemplatesKeys.all(workspaceId), "list"] as const,
  get: (workspaceId: string, templateId: string) =>
    [...caseTemplatesKeys.all(workspaceId), "template", templateId] as const,
  schema: (workspaceId: string, templateId: string) =>
    [...caseTemplatesKeys.all(workspaceId), "schema", templateId] as const,
  dataSchema: (workspaceId: string, templateId: string, versionId: string) =>
    [
      ...caseTemplatesKeys.all(workspaceId),
      "dataSchema",
      templateId,
      versionId,
    ] as const,
  version: (workspaceId: string, templateId: string, versionId: string) =>
    [
      ...caseTemplatesKeys.all(workspaceId),
      templateId,
      "version",
      versionId,
    ] as const,
};

// Type definitions
type CaseTemplateQueryOptions<TData = CaseTemplateResponse> =
  UseBaseQueryOptions<CaseTemplateResponse, AxiosError, TData>;

type CaseTemplateVersionQueryOptions<TData = CaseTemplateVersion> =
  UseBaseQueryOptions<CaseTemplateVersion, AxiosError, TData>;

// Helper hooks
const useCaseManagementClient = () => {
  const { data: workspace } = useCurrentWorkspace();

  return useMemo(
    () =>
      workspace?.base_url ? caseTemplatesApi(workspace.base_url) : undefined,
    [workspace?.base_url],
  );
};

const useWorkspaceParams = () => {
  const { wsId } = useParamsDecode<DashboardPageParams>();
  const apiClient = useCaseManagementClient();

  return { wsId, apiClient };
};

/**
 * Fetches all case templates for the current workspace
 * @param options - Query options including enabled flag
 */
export const useCaseTemplatesRaw = <TData = CaseTemplate>(
  options: Omit<
    UseQueryOptions<CaseTemplatesResponse, AxiosError, TData>,
    "queryKey" | "queryFn"
  > = { enabled: true },
) => {
  const { wsId, apiClient } = useWorkspaceParams();

  return useQuery<CaseTemplatesResponse, AxiosError, TData>({
    queryKey: caseTemplatesKeys.list(wsId),
    queryFn: async () => {
      if (!apiClient) {
        throw new Error(
          "Case management API client is not available. Please check workspace configuration.",
        );
      }

      const response = await apiClient.getCaseTemplatesApiV1TemplatesGet();

      return response.data;
    },
    staleTime: STALE_TIME,
    ...options,
    enabled: !!apiClient && options.enabled,
  });
};

const combineTemplatesWithVersions = (
  templatesResponse: CaseTemplatesResponse | undefined,
): TemplateWithVersion[] => {
  if (!templatesResponse?.data || !templatesResponse?.included) {
    return [];
  }

  return templatesResponse.data
    .flatMap((template) => {
      const templateVersions = template.relationships?.versions?.data;

      if (templateVersions && Array.isArray(templateVersions)) {
        return templateVersions.map((versionRef) => {
          const version = templatesResponse.included?.find(
            (includedItem) => includedItem.id === versionRef.id,
          );

          if (version) {
            return {
              templateId: template.id,
              versionId: version.id,
              templateName: template.attributes.template_name,
              versionName: version.attributes.version_name,
              icon: template.attributes.icon,
              template,
              version,
            };
          }

          return null;
        });
      }

      return [];
    })
    .filter(Boolean) as TemplateWithVersion[];
};

export const useAllCaseVersionsCombinedWithTemplates = (
  options = {
    enabled: true,
  },
) => {
  return useCaseTemplatesRaw({
    select: combineTemplatesWithVersions,
    enabled: options.enabled,
  });
};

const getTemplatesFromResponse = (data: CaseTemplatesResponse) => {
  return data.data;
};

export const useCaseTemplates = (
  options = {
    enabled: true,
  },
) => {
  return useCaseTemplatesRaw({
    select: getTemplatesFromResponse,
    enabled: options.enabled,
  });
};

const linkVersionsToTemplates = (data: CaseTemplatesResponse) => {
  return data.data.map((template) => {
    const versionRefs = template.relationships?.versions?.data;
    const templateVersions = Array.isArray(versionRefs)
      ? versionRefs.map((versionRef: Data) => versionRef.id)
      : [];

    return {
      ...template,
      versions: data.included?.filter((included) =>
        templateVersions.includes(included.id),
      ),
    };
  });
};

export type TemplateWithVersions = ReturnType<typeof linkVersionsToTemplates>;

export const useCaseTemplatesWithVersions = (
  options: Omit<
    UseQueryOptions<CaseTemplatesResponse, AxiosError, TemplateWithVersions>,
    "queryKey" | "queryFn" | "select"
  > = {},
) => {
  return useCaseTemplatesRaw({
    select: linkVersionsToTemplates,
    ...options,
  });
};

/**
 * Fetches JSON schema for a specific case template version
 * @param templateId - Case template ID
 * @param versionId - Template version ID
 */
export const useCaseTemplateSchema = (
  templateId: string | undefined,
  versionId: string | undefined,
) => {
  const { wsId, apiClient } = useWorkspaceParams();

  return useQuery<CaseTemplateJsonSchemaResponse, AxiosError>({
    queryKey: caseTemplatesKeys.schema(wsId, templateId || ""),
    queryFn: async () => {
      if (!apiClient || !templateId || !versionId) {
        throw new Error("Missing required parameters");
      }

      const response =
        await apiClient.getCaseTemplateVersionInputJsonSchemaApiV1TemplatesTemplateIdVersionsVersionIdCaseJsonSchemaGet(
          { templateId, versionId },
        );
      return response.data;
    },
    staleTime: STALE_TIME,
    enabled: !!apiClient && !!templateId && !!versionId,
  });
};

/**
 * Fetches data JSON schema for a specific case template version
 * @param templateId - Case template ID
 * @param versionId - Template version ID
 */
export const useCaseTemplateDataSchema = (
  templateId: string | undefined,
  versionId: string | undefined,
  options?: Omit<
    UseQueryOptions<CaseTemplateJsonSchemaResponse, AxiosError>,
    "queryKey" | "queryFn"
  >,
) => {
  const { wsId, apiClient } = useWorkspaceParams();

  return useQuery<CaseTemplateJsonSchemaResponse, AxiosError>({
    queryKey: caseTemplatesKeys.dataSchema(
      wsId,
      templateId || "",
      versionId || "",
    ),
    queryFn: async () => {
      if (!apiClient || !templateId || !versionId) {
        throw new Error("Missing required parameters");
      }

      const response =
        await apiClient.getCaseTemplateVersionDataJsonSchemaApiV1TemplatesTemplateIdVersionsVersionIdCaseDataJsonSchemaGet(
          { templateId, versionId },
        );
      return response.data;
    },
    staleTime: STALE_TIME,
    ...options,
    enabled:
      !!apiClient && !!templateId && !!versionId && options?.enabled !== false,
  });
};

// Similar to useCaseTemplateDataSchema,
// but allows to fetch data in imperative way
// and cache it right away
export const useEnsureCaseTemplateDataSchema = () => {
  const { wsId, apiClient } = useWorkspaceParams();
  const queryClient = useQueryClient();

  return useCallback(
    async (templateId: string, versionId: string) => {
      return queryClient.ensureQueryData({
        queryKey: caseTemplatesKeys.dataSchema(wsId, templateId, versionId),
        queryFn: async () => {
          if (!apiClient) {
            throw new Error("Missing required parameters");
          }

          const response =
            await apiClient.getCaseTemplateVersionDataJsonSchemaApiV1TemplatesTemplateIdVersionsVersionIdCaseDataJsonSchemaGet(
              { templateId, versionId },
            );
          return response.data;
        },
      });
    },
    [apiClient, queryClient, wsId],
  );
};

/**
 * Fetches a specific case template by ID
 * @param templateId - Case template ID
 * @param options - Additional query options
 */
export const useCaseTemplateRaw = <TData = CaseTemplateResponse>(
  templateId?: string | null,
  options?: Omit<CaseTemplateQueryOptions<TData>, "queryKey" | "queryFn">,
) => {
  const { wsId, apiClient } = useWorkspaceParams();

  return useQuery<CaseTemplateResponse, AxiosError, TData>({
    queryKey: caseTemplatesKeys.get(wsId, templateId || ""),
    queryFn: async () => {
      if (!apiClient || !templateId) {
        throw new Error("Missing required parameters");
      }

      const response =
        await apiClient.getCaseTemplateApiV1TemplatesTemplateIdGet({
          templateId,
        });
      return response.data;
    },
    staleTime: STALE_TIME,
    ...options,
    enabled: !!apiClient && !!templateId && options?.enabled !== false,
  });
};

const getTemplateFromResponse = (data: CaseTemplateResponse) => {
  return data.data;
};

export const useCaseTemplate = (
  templateId: string | undefined,
  options?: Omit<
    CaseTemplateQueryOptions<CaseTemplate>,
    "queryKey" | "queryFn"
  >,
) => {
  return useCaseTemplateRaw(templateId, {
    select: getTemplateFromResponse,
    ...options,
  });
};

const linkVersionsToTemplate = (data: CaseTemplateResponse) => {
  const template = data.data;
  const versionRefs = template.relationships?.versions?.data;
  const templateVersions = Array.isArray(versionRefs)
    ? versionRefs.map((versionRef: Data) => versionRef.id)
    : [];

  return {
    ...template,
    versions: data.included?.filter((included) =>
      templateVersions.includes(included.id),
    ),
  };
};

export const useCaseTemplateWithVersions = (templateId: string | undefined) => {
  return useCaseTemplateRaw(templateId, {
    select: linkVersionsToTemplate,
  });
};

type CaseTemplateMetadataUpdateData = CaseTemplateMetadataUpdate & {
  id: string;
  etag: string | number;
};

// Type for optimistic update context
type TemplateOptimisticUpdateContext = {
  previousTemplates?: CaseTemplatesResponse;
  previousTemplate?: CaseTemplate;
};

/**
 * Mutation hook for updating case template metadata
 * @returns Mutation object
 */
export const useCreateCaseTemplate = () => {
  const { apiClient, wsId } = useWorkspaceParams();
  const queryClient = useQueryClient();

  return useMutation<
    CaseTemplate,
    AxiosError,
    {
      template_name: string;
      description?: string;
      icon?: string;
    }
  >({
    mutationFn: async (templateData) => {
      if (!apiClient) {
        throw new Error(
          "Case management API client is not available. Please check workspace configuration.",
        );
      }

      const response = await apiClient.createCaseTemplateApiV1TemplatesPost({
        caseTemplateCreateRequest: {
          data: {
            type: "case_templates",
            attributes: {
              template_name: templateData.template_name,
              description: templateData.description,
              icon: templateData.icon,
            },
          },
        },
      });

      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: caseTemplatesKeys.list(wsId),
      });
    },
  });
};

export const useUpdateCaseTemplate = () => {
  const { apiClient, wsId } = useWorkspaceParams();
  const queryClient = useQueryClient();

  return useMutation<
    CaseTemplate,
    AxiosError,
    CaseTemplateMetadataUpdateData,
    TemplateOptimisticUpdateContext
  >({
    mutationFn: async ({ id, etag, ...template }) => {
      if (!apiClient) {
        throw new Error(
          "Case management API client is not available. Please check workspace configuration.",
        );
      }

      const response =
        await apiClient.updateCaseTemplateMetadataApiV1TemplatesTemplateIdPut({
          templateId: id,
          caseTemplateMetadataUpdateRequest: {
            data: {
              type: "case_templates",
              attributes: {
                template_name: template.template_name,
                description: template.description,
                icon: template.icon,
              },
            },
          },
          ifMatch: String(etag),
        });

      return response.data.data;
    },
    onMutate: async ({ id, ...newData }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: caseTemplatesKeys.list(wsId),
      });
      await queryClient.cancelQueries({
        queryKey: caseTemplatesKeys.get(wsId, id),
      });

      // Snapshot previous values
      const previousTemplates = queryClient.getQueryData<CaseTemplatesResponse>(
        caseTemplatesKeys.list(wsId),
      );
      const previousTemplate = queryClient.getQueryData<CaseTemplate>(
        caseTemplatesKeys.get(wsId, id),
      );

      // Optimistically update templates list
      if (previousTemplates) {
        queryClient.setQueryData<CaseTemplatesResponse>(
          caseTemplatesKeys.list(wsId),
          {
            ...previousTemplates,
            data: previousTemplates.data.map((template) =>
              template.id === id
                ? {
                    ...template,
                    attributes: {
                      ...template.attributes,
                      template_name: newData.template_name,
                      description: newData.description,
                      icon: newData.icon,
                    },
                  }
                : template,
            ),
          },
        );
      }

      // Optimistically update single template
      if (previousTemplate) {
        queryClient.setQueryData<CaseTemplate>(
          caseTemplatesKeys.get(wsId, id),
          {
            ...previousTemplate,
            attributes: {
              ...previousTemplate.attributes,
              template_name: newData.template_name,
              description: newData.description,
              icon: newData.icon,
            },
          },
        );
      }

      return { previousTemplates, previousTemplate };
    },
    onError: (_, variables, context) => {
      // Rollback optimistic updates on error
      if (context?.previousTemplates) {
        queryClient.setQueryData(
          caseTemplatesKeys.list(wsId),
          context.previousTemplates,
        );
      }
      if (context?.previousTemplate) {
        queryClient.setQueryData(
          caseTemplatesKeys.get(wsId, variables.id),
          context.previousTemplate,
        );
      }
    },
    onSettled: (data) => {
      // Always refetch after mutation settles to ensure consistency
      queryClient.invalidateQueries({
        queryKey: caseTemplatesKeys.list(wsId),
      });
      if (data?.id) {
        queryClient.invalidateQueries({
          queryKey: caseTemplatesKeys.get(wsId, data.id),
        });
      }
    },
  });
};

type DuplicateTemplateForm = {
  templateId: string;
  versionId: string;
  versionName: string;
};

export const useDuplicateCaseTemplateVersion = () => {
  const { apiClient, wsId } = useWorkspaceParams();
  const queryClient = useQueryClient();

  return useMutation<
    CaseTemplateVersion,
    AxiosError,
    DuplicateTemplateForm,
    TemplateOptimisticUpdateContext
  >({
    mutationFn: async ({ templateId, versionId, versionName }) => {
      if (!apiClient) {
        throw new Error("Missing required parameters");
      }

      const response =
        await apiClient.duplicateCaseTemplateVersionApiV1TemplatesTemplateIdVersionsVersionIdDuplicatePost(
          {
            templateId,
            versionId,
            caseTemplateVersionDuplicateRequest: {
              data: {
                type: "case_template_versions",
                attributes: {
                  new_version_name: versionName,
                },
              },
            },
          },
        );

      const newVersion = response.data.data;

      const fileCategories = newVersion.attributes.file_categories || {};
      const hasOtherFiles = "other_files" in fileCategories;

      if (!hasOtherFiles) {
        // Add the default "Other Files" category
        const updateResponse =
          await apiClient.updateCaseTemplateVersionApiV1TemplatesTemplateIdVersionsVersionIdPatch(
            {
              templateId,
              versionId: newVersion.id,
              caseTemplateVersionUpdateRequest: {
                data: {
                  type: "case_template_versions",
                  attributes: {
                    file_categories: {
                      ...fileCategories,
                      other_files: {
                        display_name: "Other Files",
                        description:
                          "Upload additional files supporting the case review.",
                      },
                    },
                  },
                },
              },
              ifMatch: String(newVersion.attributes.etag ?? 0),
            },
          );
        return updateResponse.data.data;
      }

      return newVersion;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: caseTemplatesKeys.list(wsId),
      });
      queryClient.invalidateQueries({
        queryKey: caseTemplatesKeys.get(wsId, variables.templateId),
      });
    },
  });
};

export type CaseTemplateVersionWithBlocks = CaseTemplateVersion & {
  case_blocks_configs: Record<string, CaseBlockConfigOutput>;
};

/**
 * Fetches a specific version of a case template
 * @param params - Template and version IDs
 * @param options - Additional query options
 */
export const useCaseTemplateVersion = <TData = CaseTemplateVersion>(
  params: {
    templateId: string | undefined;
    versionId: string | undefined;
  },
  options?: Omit<
    CaseTemplateVersionQueryOptions<TData>,
    "queryKey" | "queryFn"
  >,
) => {
  const { templateId, versionId } = params;
  const { wsId, apiClient } = useWorkspaceParams();

  return useQuery<CaseTemplateVersion, AxiosError, TData>({
    queryKey: caseTemplatesKeys.version(
      wsId,
      templateId || "",
      versionId || "",
    ),
    queryFn: async () => {
      if (!apiClient || !templateId || !versionId) {
        throw new Error("Missing required parameters");
      }

      const response =
        await apiClient.getCaseTemplateVersionApiV1TemplatesTemplateIdVersionsVersionIdGet(
          { templateId, versionId },
        );

      return response.data.data;
    },
    staleTime: STALE_TIME,
    ...options,
    enabled:
      !!apiClient && !!templateId && !!versionId && options?.enabled !== false,
  });
};

type CaseTemplateVersionUpdateData = {
  templateId: string;
  versionId: string;
  versionAttributes: Partial<CaseTemplateVersionAttributes>;
  etag: string | number | undefined;
};

type TemplateVersionOptimisticUpdateContext = {
  previousVersion?: CaseTemplateVersion;
};

export const useUpdateCaseTemplateVersion = () => {
  const { apiClient, wsId } = useWorkspaceParams();
  const queryClient = useQueryClient();

  return useMutation<
    CaseTemplateVersion,
    AxiosError,
    CaseTemplateVersionUpdateData,
    TemplateVersionOptimisticUpdateContext
  >({
    mutationFn: async ({ templateId, versionId, etag, versionAttributes }) => {
      if (!apiClient) {
        throw new Error("Missing required parameters");
      }

      const response =
        await apiClient.updateCaseTemplateVersionApiV1TemplatesTemplateIdVersionsVersionIdPatch(
          {
            templateId,
            versionId,
            caseTemplateVersionUpdateRequest: {
              data: {
                type: "case_template_versions",
                attributes: versionAttributes,
              },
            },
            ifMatch: String(etag ?? 0),
          },
        );

      return response.data.data;
    },
    onMutate: async ({ templateId, versionId, versionAttributes }) => {
      await queryClient.cancelQueries({
        queryKey: caseTemplatesKeys.version(wsId, templateId, versionId),
      });

      const previousVersion = queryClient.getQueryData<CaseTemplateVersion>(
        caseTemplatesKeys.version(wsId, templateId, versionId),
      );

      queryClient.setQueryData(
        caseTemplatesKeys.version(wsId, templateId, versionId),
        {
          ...(previousVersion ?? {}),
          attributes: {
            ...(previousVersion?.attributes ?? {}),
            ...versionAttributes,
          },
        },
      );

      return { previousVersion };
    },
    onError: (_, variables, context) => {
      if (context?.previousVersion) {
        queryClient.setQueryData(
          caseTemplatesKeys.version(
            wsId,
            variables.templateId,
            variables.versionId,
          ),
          context.previousVersion,
        );
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: caseTemplatesKeys.list(wsId),
      });
      queryClient.invalidateQueries({
        queryKey: caseTemplatesKeys.get(wsId, variables.templateId),
      });
      queryClient.invalidateQueries({
        queryKey: caseTemplatesKeys.version(
          wsId,
          variables.templateId,
          variables.versionId,
        ),
      });
    },
  });
};

export const usePublishCaseTemplateVersion = () => {
  const {
    mutateAsync: updateCaseTemplateVersionAsync,
    mutate: updateCaseTemplateVersion,
    ...mutation
  } = useUpdateCaseTemplateVersion();

  const mutateAsync = useCallback(
    ({
      name,
      ...data
    }: Omit<CaseTemplateVersionUpdateData, "versionAttributes"> & {
      name: string;
    }) => {
      return updateCaseTemplateVersionAsync({
        ...data,
        versionAttributes: {
          publish_state: CaseTemplateVersionPublishState.PUBLISHED,
          version_name: name,
        },
      });
    },
    [updateCaseTemplateVersionAsync],
  );

  const mutate = useCallback(
    ({
      name,
      ...data
    }: Omit<CaseTemplateVersionUpdateData, "versionAttributes"> & {
      name: string;
    }) => {
      return updateCaseTemplateVersion({
        ...data,
        versionAttributes: {
          publish_state: CaseTemplateVersionPublishState.PUBLISHED,
          version_name: name,
        },
      });
    },
    [updateCaseTemplateVersion],
  );

  return {
    ...mutation,
    mutateAsync,
    mutate,
  };
};

export const useArchiveCaseTemplateVersion = () => {
  const {
    mutateAsync: updateCaseTemplateVersionAsync,
    mutate: updateCaseTemplateVersion,
    ...mutation
  } = useUpdateCaseTemplateVersion();

  const mutateAsync = useCallback(
    (data: Omit<CaseTemplateVersionUpdateData, "versionAttributes">) => {
      return updateCaseTemplateVersionAsync({
        ...data,
        versionAttributes: {
          archived: true,
        },
      });
    },
    [updateCaseTemplateVersionAsync],
  );

  const mutate = useCallback(
    (data: Omit<CaseTemplateVersionUpdateData, "versionAttributes">) => {
      return updateCaseTemplateVersion({
        ...data,
        versionAttributes: {
          archived: true,
        },
      });
    },
    [updateCaseTemplateVersion],
  );

  return {
    ...mutation,
    mutateAsync,
    mutate,
  };
};
